import { useState, useEffect, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { Clock, Square, Coffee, AlertTriangle, X } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { ratioToneClass } from '@/styles/colorUtils.ts'
import { Button, IconButton } from '@/components/ui'

interface TimeStatus {
  isClockedIn: boolean
  isOnBreak: boolean
  currentEntry: { startTime: string } | null
  pauseMinutesToday: number
  documentedMinutesToday: number
  workMinutesToday: number
}

interface OvertimeData {
  weeklyHours: number
  targetWeeklyHours: number
  totalOvertimeMinutes: number
  monthBalance: number
}

function formatMinutes(mins: number): string {
  const h = Math.floor(Math.abs(mins) / 60)
  const m = Math.abs(mins) % 60
  return `${h}h ${String(m).padStart(2, '0')}min`
}

function formatSaldo(mins: number): string {
  const sign = mins >= 0 ? '+' : '-'
  const h = Math.floor(Math.abs(mins) / 60)
  const m = Math.abs(mins) % 60
  return `${sign}${h}:${String(m).padStart(2, '0')}h`
}

interface ClockResponse {
  tanssSynced?: boolean
  tanssError?: string
}

export function ClockWidget({ greeting, dateStr, standTime }: { greeting?: string; dateStr?: string; standTime?: string }) {
  const qc = useQueryClient()
  const [, setTick] = useState(0)
  const [tanssWarning, setTanssWarning] = useState<string | null>(null)

  const handleTanssResponse = useCallback((res: { data: ClockResponse }) => {
    if (res.data.tanssSynced === false) {
      setTanssWarning(res.data.tanssError || 'TANSS-Sync fehlgeschlagen')
      setTimeout(() => setTanssWarning(null), 8000)
    } else {
      setTanssWarning(null)
    }
    qc.invalidateQueries({ queryKey: ['time'] })
  }, [qc])

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000)
    return () => clearInterval(interval)
  }, [])

  const { data: status } = useQuery<TimeStatus>({
    queryKey: ['time', 'status'],
    queryFn: () => apiClient.get('/time/status').then((r) => r.data),
    refetchInterval: 30 * 1000,
  })

  const { data: overtime } = useQuery<OvertimeData>({
    queryKey: ['time', 'overtime'],
    queryFn: () => apiClient.get('/time/overtime').then((r) => r.data),
    refetchInterval: 60 * 1000,
    retry: false,
  })

  const clockIn = useMutation({
    mutationFn: () => apiClient.post('/time/clock-in'),
    onSuccess: handleTanssResponse,
  })

  const clockOut = useMutation({
    mutationFn: () => apiClient.post('/time/clock-out'),
    onSuccess: handleTanssResponse,
  })

  const pauseStart = useMutation({
    mutationFn: () => apiClient.post('/time/pause'),
    onSuccess: handleTanssResponse,
  })

  const pauseEnd = useMutation({
    mutationFn: () => apiClient.post('/time/pause/end'),
    onSuccess: handleTanssResponse,
  })

  const isClockedIn = status?.isClockedIn ?? false
  const isOnBreak = status?.isOnBreak ?? false
  const startTime = status?.currentEntry?.startTime
    ? new Date(status.currentEntry.startTime)
    : null

  const elapsed = startTime
    ? Math.floor((Date.now() - startTime.getTime()) / 60000)
    : 0

  const pauseMinutes = status?.pauseMinutesToday ?? 0
  const workMinutes = status?.workMinutesToday ?? 0
  const documentedMinutes = status?.documentedMinutesToday ?? 0

  const docRatio = workMinutes > 0 ? documentedMinutes / workMinutes : 0
  const docColor = ratioToneClass(docRatio)

  const weeklyHours = overtime?.weeklyHours ?? 0
  const targetWeeklyHours = overtime?.targetWeeklyHours ?? 40
  const totalOvertimeMinutes = overtime?.totalOvertimeMinutes ?? 0

  return (
    <div className="bg-gray-900 rounded-md px-4 py-2 flex items-center gap-3 flex-wrap text-white w-full">
      {greeting && (
        <>
          <div className="flex items-center gap-2">
            <div>
              <span className="text-body font-bold text-white">{greeting}</span>
              {dateStr && <span className="text-caption text-gray-400 ml-2">{dateStr}</span>}
            </div>
          </div>
          <div className="h-4 w-px bg-gray-700" />
        </>
      )}

      <div className="flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-caption font-bold text-gray-400 uppercase">ZE</span>
      </div>

      <Divider />

      {startTime && (
        <>
          <InfoBlock
            label="Kommen"
            value={startTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            valueColor="text-green-400"
          />
          <Divider />
        </>
      )}

      <div className="flex items-center gap-1.5">
        <span className={cn(
          'w-2 h-2 rounded-full',
          isOnBreak ? 'bg-yellow-400' : isClockedIn ? 'bg-green-400' : 'bg-gray-500',
        )} />
        <span className="text-caption text-gray-400">Status</span>
        <span className={cn(
          'text-caption font-bold',
          isOnBreak ? 'text-yellow-400' : isClockedIn ? 'text-green-400' : 'text-gray-500',
        )}>
          {isOnBreak ? 'Pause' : isClockedIn ? 'anwesend' : 'abwesend'}
        </span>
      </div>

      <Divider />

      <InfoBlock label="Pause" value={formatMinutes(pauseMinutes)} />

      <Divider />

      <InfoBlock
        label="bisher"
        value={formatMinutes(elapsed)}
        valueColor="text-white"
        bold
      />

      {workMinutes > 0 && (
        <>
          <span className="text-gray-600 text-caption">/</span>
          <div className="flex items-center gap-1">
            <span className="text-caption text-gray-500">dok.</span>
            <span className={cn('text-caption font-bold', docColor)}>
              {formatMinutes(documentedMinutes)}
            </span>
          </div>
        </>
      )}

      <div className="h-4 w-px bg-gray-700 mx-1" />

      <div className="flex items-center gap-1">
        <span className="text-caption text-gray-400">Woche</span>
        <span className="text-body font-bold text-white">
          {weeklyHours.toFixed(1)}h
        </span>
        <span className="text-caption text-gray-500">/{targetWeeklyHours}h</span>
      </div>

      <Divider />

      <InfoBlock
        label="Saldo"
        value={formatSaldo(totalOvertimeMinutes)}
        valueColor={totalOvertimeMinutes >= 0 ? 'text-green-400' : 'text-red-400'}
      />

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        {!isClockedIn ? (
          <Button
            variant="primary"
            size="xs"
            onClick={() => clockIn.mutate()}
            loading={clockIn.isPending}
          >
            Einstempeln
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              size="xs"
              leadingIcon={<Square className="w-3 h-3" />}
              onClick={() => clockOut.mutate()}
              loading={clockOut.isPending}
            >
              Ausstempeln
            </Button>
            {isOnBreak ? (
              <Button
                variant="secondary"
                size="xs"
                leadingIcon={<Coffee className="w-3 h-3" />}
                onClick={() => pauseEnd.mutate()}
                loading={pauseEnd.isPending}
              >
                Fortsetzen
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="xs"
                leadingIcon={<Coffee className="w-3 h-3" />}
                onClick={() => pauseStart.mutate()}
                loading={pauseStart.isPending}
              >
                Pause
              </Button>
            )}
          </>
        )}
      </div>

      {standTime && (
        <span className="text-caption text-gray-500">Stand: {standTime}</span>
      )}

      {tanssWarning && (
        <div className="w-full flex items-center gap-2 bg-yellow-900/40 border border-yellow-700/50 rounded px-3 py-1.5 mt-1">
          <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
          <span className="text-caption text-yellow-300">TANSS: {tanssWarning}</span>
          <IconButton
            icon={<X />}
            aria-label="Warnung schließen"
            size="xs"
            onClick={() => setTanssWarning(null)}
            className="ml-auto text-yellow-500 hover:text-yellow-300 hover:bg-yellow-900/30"
          />
        </div>
      )}
    </div>
  )
}

function InfoBlock({
  label,
  value,
  valueColor = 'text-gray-200',
  bold,
}: {
  label: string
  value: string
  valueColor?: string
  bold?: boolean
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-caption text-gray-400">{label}</span>
      <span className={cn('text-caption font-bold', bold && 'text-body', valueColor)}>
        {value}
      </span>
    </div>
  )
}

function Divider() {
  return <span className="text-gray-700 text-caption select-none">·</span>
}
